import type { CheckboxProps } from '@_/ui';
import type { CheckboxTreeContextType } from './CheckboxTreeContext.ts';
import type { CheckboxTreeNode } from './types.ts';
import { use, useCallback, useMemo } from 'react';
import { LuChevronDown } from 'react-icons/lu';
import { Checkbox, Collapsible, CollapsiblePanel, CollapsibleTrigger } from '@_/ui';
import { CheckboxTreeContext } from './CheckboxTreeContext.ts';

function getLeafNodeIds<TValue>(
  nodes: Array<CheckboxTreeNode<TValue>>
): Array<CheckboxTreeNode<TValue>['id']> {
  return nodes
    .map(node => (node.children?.length ? getLeafNodeIds<TValue>(node.children) : node.id))
    .flat();
}

interface CheckboxTreeItemProps<TValue> {
  node: CheckboxTreeNode<TValue>;
}

export function CheckboxTreeItem<TValue>({ node }: CheckboxTreeItemProps<TValue>) {
  const { disabled, onChange, renderLabel, selectedIds } = use(
    CheckboxTreeContext as unknown as React.Context<CheckboxTreeContextType<TValue>>
  );

  const leafNodeIds = useMemo(
    () => (node.children?.length ? getLeafNodeIds(node.children) : []),
    [node]
  );

  const checked = useMemo<CheckboxProps['checked']>(() => {
    if (node.children?.length) {
      if (leafNodeIds.every(leafId => selectedIds.includes(leafId))) {
        return true;
      }
      if (leafNodeIds.some(leafId => selectedIds.includes(leafId))) {
        return 'indeterminate';
      }
      return false;
    }
    return selectedIds.includes(node.id);
  }, [leafNodeIds, node, selectedIds]);

  const handleClick = useCallback<Exclude<CheckboxProps['onClick'], undefined>>(event => {
    event.stopPropagation();
  }, []);

  const handleToggle = () => {
    let updatedSelectedIds: Array<CheckboxTreeNode['id']>;
    if (node.children?.length) {
      updatedSelectedIds = selectedIds.filter(selectedId => !leafNodeIds.includes(selectedId));
      if (!checked) {
        updatedSelectedIds = [...updatedSelectedIds, ...leafNodeIds];
      }
    } else {
      updatedSelectedIds = selectedIds.includes(node.id)
        ? selectedIds.filter(selectedId => selectedId !== node.id)
        : [...selectedIds, node.id];
    }
    onChange?.(updatedSelectedIds);
  };

  const checkboxMarkup = (
    <Checkbox
      checked={checked}
      disabled={disabled}
      label={renderLabel(node.value)}
      onChange={handleToggle}
      onClick={handleClick}
      value={node.id}
    />
  );

  return node.children?.length ? (
    <Collapsible>
      <CollapsibleTrigger
        nativeButton={false}
        render={<div />}
        className="flex cursor-pointer items-center justify-between text-gray-700"
      >
        <span className="-m-1 min-w-0 truncate p-1">{checkboxMarkup}</span>

        <LuChevronDown
          aria-hidden="true"
          className="ml-3 size-5 transition-transform duration-200 ease-in-out group-data-panel-open/collapsible-trigger:rotate-180 motion-reduce:transition-none"
        />
      </CollapsibleTrigger>
      <CollapsiblePanel className="flex flex-col gap-y-4 pt-4 pb-1 pl-4">
        {node.children.map(child => (
          <CheckboxTreeItem key={child.id} node={child} />
        ))}
      </CollapsiblePanel>
    </Collapsible>
  ) : (
    <div className="text-gray-700">{checkboxMarkup}</div>
  );
}
